import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  TextField,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { get } from "lodash";
import { login } from "@smfa/utils/src";

/**
 * Admin Login
 * @returns {JSX.Element}
 * @component
 */
const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(
    useSelector((state) => state.pageContents?.loading),
  );

  const auth = useSelector((state) => state.auth) || {};
  const user = useSelector((state) => state.user) || {};
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  log.debug("user login: Redux auth state:", auth, "user state:", user);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const userRole = get(user, "data.role");

  useEffect(() => {
    if (isAuthenticated && userRole === "admin") {
      // Only redirect for valid admin users
      navigate("/home");
    }
  }, [userRole, isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      borderRadius: "16px !important",
      backgroundColor: "#ffffff",
      boxShadow: "0 10px 24px rgba(0, 20, 48, 0.08)",
      borderLeft: "3px solid #0cb8b6 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 20, 48, 0.14)",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 20, 48, 0.28)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0cb8b6",
      borderWidth: "1.5px",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(formData, {
        dispatch,
        onSuccess: ({ token, user }) => {
          log.debug("[admin] token:", token);

          navigate("/home");
        },
        onError: ({ message, response }) => {
          setError(message);
          log.error("[admin] Login error:", response);
        },
        requireRole: "admin",
      });
      log.debug("[admin] Login result:", result);
    } catch (err) {
      log.error("[admin] Login error:", err);
      setError(err.message || "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, sm: 6 },
        background: `
          radial-gradient(circle at top left, rgba(12, 184, 182, 0.12), transparent 34%),
          radial-gradient(circle at bottom right, rgba(226, 154, 18, 0.10), transparent 28%),
          linear-gradient(180deg, #ffffff 0%, #f8f8f8 100%)
        `,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 4,
            border: "1px solid rgba(12, 184, 182, 0.16)",
            borderTop: "6px solid #0cb8b6",
            boxShadow: "0 28px 70px rgba(0, 20, 48, 0.16)",
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4.5 } }}>
            <Stack
              spacing={3}
              sx={{ alignItems: "center", textAlign: "center" }}
            >
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 700, color: "#001430" }}
                >
                  Admin Login
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, maxWidth: 360, mx: "auto" }}
                >
                  Sign in with an administrator account to manage content,
                  memberships, and site settings.
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                    variant="outlined"
                    sx={fieldSx}
                  />
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                    variant="outlined"
                    sx={fieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePassword}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    sx={{
                      mt: 0.5,
                      minHeight: 48,
                      borderRadius: 999,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 700,
                      backgroundColor: "#0cb8b6",
                      boxShadow: "0 16px 32px rgba(12, 184, 182, 0.24)",
                      "&:hover": {
                        backgroundColor: "#0aa3a1",
                        boxShadow: "0 18px 36px rgba(12, 184, 182, 0.30)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
